// src/components/Wallet/ConnectWallet.js

import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { NFTContext } from '../../context/NFTContext';
import { BoosterNFTContext } from '../../context/BoosterNFTContext';
import { WalletContext } from '../../context/WalletContext';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Badge,
  Modal,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';
import '../../App.css';
import { toast } from 'react-toastify';
import { CASHNFT_ADDRESS, BOOSTERNFT_ADDRESS, NETWORK_CONFIG } from '../../contracts/constants';
import BoosterNFTMinter from '../NFT/BoosterNFTMinter'; 

const InlinePlaceholderSVG = () => (
  <svg width="100%" height="100%" viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="45" fill="#ccc" />
    <text x="50%" y="50%" fontSize="20" textAnchor="middle" fill="#666" dy=".3em">
      NFT
    </text>
  </svg>
);

const ConnectWallet = ({ changeWalletFontSize = '0.65rem' }) => {
  const {
    account,
    connectMetaMask,
    changeAccount,
    isChangingWallet,
    chainId,
  } = useContext(WalletContext);

  const {
    ogMinerNFTs,
    og49erNFTs,
    ogSwapperNFTs,
    councilNFTs,
    hasMintedCouncil,
    isOGMinerEligible,
    isOG49erEligible,
    isOGSwapperEligible,
    isCouncilEligible,
    mintOGMinerNFT,
    mintOG49erNFT,
    mintOGSwapperNFT,
    mintCouncilNFT,
    fetchUserNFTs,
    determineEligibility,
    isTransactionPending,
  } = useContext(NFTContext);

  const { boosterNFTs } = useContext(BoosterNFTContext);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false); 
  const [selectedNFTType, setSelectedNFTType] = useState(null); 

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleSelectNFTType = (type) => {
    setSelectedNFTType(type);
  };

  const handleConnect = useCallback(async () => {
    if (account) return;
    setError('');
    setLoading(true);
    try {
      const success = await connectMetaMask(true);
      if (success) {
        await fetchUserNFTs();
        await determineEligibility();
      } else {
        setError('Failed to connect wallet.');
        toast.error('Failed to connect wallet.', {
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (err) {
      setError('Failed to connect wallet.');
      toast.error('Failed to connect wallet.', {
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  }, [account, connectMetaMask, fetchUserNFTs, determineEligibility]);

  const handleChangeWallet = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const success = await changeAccount();
      if (success) {
        await fetchUserNFTs();
        await determineEligibility();
      }
    } catch (err) {
      console.error('Error in handleChangeWallet:', err);
      setError('Failed to change wallet.');
      toast.error('Failed to change wallet. Please try again.', {
        autoClose: 3000,
        hideProgressBar: true,
      });
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  }, [changeAccount, fetchUserNFTs, determineEligibility]);

  useEffect(() => {
    if (account) {
      fetchUserNFTs();
      determineEligibility();
    }
  }, [account, fetchUserNFTs, determineEligibility]);

  const nfts = useMemo(
    () => [
      {
        name: 'Booster NFT',
        list: boosterNFTs || [],
        mintFunction: null,
        eligible: false,
        contractAddress: BOOSTERNFT_ADDRESS,
        description: 'Booster NFTs provide enhanced staking rewards and special features.',
      },
      {
        name: 'OG Miner',
        list: ogMinerNFTs,
        mintFunction: mintOGMinerNFT,
        eligible: isOGMinerEligible,
        contractAddress: CASHNFT_ADDRESS,
        description: 'OG Miner NFTs are awarded to early miners in the CASH ecosystem.',
      },
      {
        name: 'OG 49er',
        list: og49erNFTs,
        mintFunction: mintOG49erNFT,
        eligible: isOG49erEligible,
        contractAddress: CASHNFT_ADDRESS,
        description: 'OG 49er NFTs recognize significant early contributors.',
      },
      {
        name: 'OG Swapper',
        list: ogSwapperNFTs,
        mintFunction: mintOGSwapperNFT,
        eligible: isOGSwapperEligible,
        contractAddress: CASHNFT_ADDRESS,
        description: 'OG Swapper NFTs are for early adopters who used the CASH swap.',
      },
      {
        name: 'Council NFT',
        list: councilNFTs,
        mintFunction: mintCouncilNFT,
        eligible: isCouncilEligible,
        contractAddress: CASHNFT_ADDRESS,
        description: 'Council NFTs provide governance rights and a 10% staking bonus.',
      },
    ],
    [
      boosterNFTs,
      ogMinerNFTs,
      og49erNFTs,
      ogSwapperNFTs,
      councilNFTs,
      isOGMinerEligible,
      isOG49erEligible,
      isOGSwapperEligible,
      isCouncilEligible,
      mintOGMinerNFT,
      mintOG49erNFT,
      mintOGSwapperNFT,
      mintCouncilNFT,
    ]
  );

  const ownedNFTs = nfts.filter(nft => nft.list && nft.list.length > 0);
  const hasAnyNFT = ownedNFTs.length > 0;
  const totalNFTCount = nfts.reduce((total, nft) => total + (nft.list?.length || 0), 0);

  const processedNFTs = useMemo(() => {
    return nfts.map(nft => {
      if (nft.name === 'Booster NFT' && nft.list && nft.list.length > 0) {
        return {
          ...nft,
          list: nft.list.map(item => ({
            ...item,
            imageSrc: item.metadata?.image || "https://via.placeholder.com/500?text=Booster+NFT",
            tokenId: item.tokenId || item.id
          }))
        };
      }
      return nft;
    });
  }, [nfts]);

  const renderModalContent = () => {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '80%' },
          maxWidth: '1000px',
          maxHeight: '80vh',
          bgcolor: 'var(--background-light)',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflow: 'auto'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2" sx={{ color: 'var(--primary-color)' }}>
            Your NFT Collection
          </Typography>
          <IconButton onClick={handleCloseModal} aria-label="close">
            <CloseIcon sx={{ color: 'var(--primary-color)' }}/>
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'var(--primary-color)' }} />

        <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
          <Button
            variant={selectedNFTType === null ? "contained" : "outlined"}
            onClick={() => handleSelectNFTType(null)}
            size="small"
            sx={{
              borderColor: 'var(--primary-color)',
              color: 'var(--primary-color)',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#ffcc00',
                color: '#1a1a1a',
                boxShadow: '0 0 8px #ffcc00',
              }
            }}
          >
            All ({totalNFTCount})
          </Button>
          {processedNFTs.map((nft, idx) => (
            nft.list && nft.list.length > 0 && (
              <Button
                key={idx}
                variant={selectedNFTType === nft.name ? "contained" : "outlined"}
                onClick={() => handleSelectNFTType(nft.name)}
                size="small"
                sx={{
                  borderColor: 'var(--primary-color)',
                  color: 'var(--primary-color)',
                  textTransform: 'uppercase',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#ffcc00',
                    color: '#1a1a1a',
                    boxShadow: '0 0 8px #ffcc00',
                  }
                }}
              >
                {nft.name} ({nft.list.length})
              </Button>
            )
          ))}
        </Box>

        <Grid container spacing={2}>
          {processedNFTs
            .filter(
              nft =>
                nft.list &&
                nft.list.length > 0 &&
                (selectedNFTType === null || selectedNFTType === nft.name)
            )
            .map((nft, nftIdx) => (
              <React.Fragment key={nftIdx}>
                {nft.list.map((item, itemIdx) => (
                  <Grid item xs={6} sm={4} md={3} key={`${nft.name}-${itemIdx}`}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid var(--primary-color)',
                        borderRadius: 2,
                        backgroundColor: 'var(--background-light)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 0 10px var(--primary-color)',
                        }
                      }}
                    >
                      <CardMedia
                        component="div"
                        sx={{
                          pt: '75%',
                          position: 'relative',
                          bgcolor: '#f5f5f5',
                          overflow: 'hidden'
                        }}
                      >
                        {item.imageSrc ? (
                          <img
                            src={item.imageSrc}
                            alt={`${nft.name} #${item.tokenId}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/500?text=Image+Error";
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <InlinePlaceholderSVG />
                          </Box>
                        )}
                        {nft.name === 'Council NFT' && hasMintedCouncil && (
                          <Badge
                            badgeContent="10% Bonus"
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              '& .MuiBadge-badge': {
                                fontSize: '0.6rem',
                                padding: '0 6px',
                              }
                            }}
                          />
                        )}
                      </CardMedia>
                      <CardContent sx={{ flexGrow: 1, p: 1 }}>
                        <Typography
                          gutterBottom
                          variant="subtitle2"
                          component="div"
                          sx={{ fontWeight: 'bold', color: 'var(--primary-color)' }}
                        >
                          {nft.name} #{item.tokenId}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ffffff' }}>
                          {nft.description}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ 
                            mt: 1, 
                            borderColor: 'var(--primary-color)', 
                            color: 'var(--primary-color)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: '#ffcc00',
                              color: '#1a1a1a',
                            }
                          }}
                          onClick={() => {
                            const blockExplorerLink = `${NETWORK_CONFIG.blockExplorer}/token/${nft.contractAddress}/instance/${item.tokenId}`;
                            window.open(blockExplorerLink, '_blank');
                          }}
                        >
                          View on Explorer
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </React.Fragment>
            ))}
        </Grid>

        {(!hasAnyNFT ||
          (selectedNFTType &&
            !processedNFTs.find(n => n.name === selectedNFTType)?.list?.length)) && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              {selectedNFTType
                ? `No ${selectedNFTType} NFTs found`
                : 'No NFTs found in your wallet'}
            </Typography>
            {!hasAnyNFT && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                Check your eligibility to claim NFTs or acquire them through the platform.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const isCorrectNetwork = Number(chainId) === NETWORK_CONFIG.chainId;

  return (
    <Box className="wallet-container" display="flex" flexDirection="column" alignItems="center" mt={2}>
      {!account ? (
        <Button
          onClick={handleConnect}
          variant="contained"
          disabled={loading || isTransactionPending}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            px: 2,
            transition: 'all 0.2s ease-in-out',
            backgroundColor: '#ff3333',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#cc0000',
              boxShadow: '0 0 8px #cc0000'
            }
          }}
        >
          {loading || isTransactionPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Connect Wallet'
          )}
        </Button>
      ) : !isCorrectNetwork ? (
        <Typography color="error" variant="body2" align="center" sx={{ mb: 1 }}>
          Please connect to {NETWORK_CONFIG.name}
        </Typography>
      ) : (
        <Box className="wallet-info" display="flex" flexDirection="column" alignItems="center">
          <Typography
            variant="body2"
            className="wallet-address"
            fontSize="10px"
            aria-label="Wallet Address"
          >
            {`${account.substring(0, 6)}...${account.substring(account.length - 4)}`}
          </Typography>
          <Box className="wallet-actions" mt={1}>
            <Button
              onClick={handleChangeWallet}
              variant="contained"
              disabled={loading || isTransactionPending || isChangingWallet}
              className="wallet-button change-wallet-button"
              sx={{
                fontSize: changeWalletFontSize,
                transition: 'all 0.2s ease-in-out',
                backgroundColor: '#4a4a4a',
                color: '#ffcc00',
                '&:hover': {
                  backgroundColor: '#ffcc00',
                  color: '#1a1a1a',
                  boxShadow: '0 0 10px #ffcc00'
                }
              }}
              aria-label="Change Wallet"
            >
              {loading || isTransactionPending || isChangingWallet ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Change Wallet'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" mt={2}>
          {error}
        </Typography>
      )}

      {account && isCorrectNetwork && (
        <>
          <Box
            onClick={handleOpenModal}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              mt: 2,
              p: 1,
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(255, 204, 0, 0.1)',
                boxShadow: '0 0 8px rgba(255, 204, 0, 0.5)',
              }
            }}
          >
            <Badge
              badgeContent={totalNFTCount}
              color="primary"
              showZero
              max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
            >
              <CollectionsIcon sx={{ fontSize: 30, mb: 0.5 }} />
            </Badge>
            <Typography variant="caption">
              {hasAnyNFT ? 'View Your NFTs' : 'No NFTs Found'}
            </Typography>
          </Box>

          <Box mt={1}>
            <BoosterNFTMinter compact={true} />
          </Box>

          <Modal
            open={openModal}
            onClose={handleCloseModal}
            aria-labelledby="nft-modal-title"
            aria-describedby="nft-modal-description"
          >
            {renderModalContent()}
          </Modal>
        </>
      )}
    </Box>
  );
};

export default ConnectWallet;